import React, { Dispatch, ReactNode, SetStateAction, useState } from 'react';
import styled, { css } from 'styled-components';
import { IoCloseOutline } from 'react-icons/io5';
import { IoIosLink } from 'react-icons/io';
import { useRouteHandler } from '../../hooks/useRouteHandler';
import { darken } from '../../lib/polished';
import theme from '@styles/theme';
import SearchModal from '../modal/SearchModal';

type HeadlineProps = {
	isSearch?: boolean;
	link?: string;
	title?: string; // 제목
	titleDescription?: string; // 제목 설명
	subTitle?: string; // 부제목
	search?: string; // 검색어 상태
	setSearch?: Dispatch<SetStateAction<string>>;
	searchCount?: number;
	handleSearch?: (value: string) => void;
	buttonList?: ReactNode[]; // 버튼 목록 (문자열 외에, 스스로 갱신되는 라벨 컴포넌트도 넣을 수 있다)
	buttonHandler?: (() => void)[]; // 버튼 함수
};

const Headline = ({
	isSearch,
	link,
	title: propsTitle,
	subTitle: propsSubTitle,
	titleDescription,
	search,
	setSearch = () => null,
	searchCount = 0,
	handleSearch = () => null,
	buttonList = [],
	buttonHandler = []
}: HeadlineProps) => {
	const [searchModal, setSearchModal] = useState(false);
	const { title: routeTitle, subTitle: routeSubTitle } = useRouteHandler('current');

	const title = propsTitle || routeTitle;
	const subTitle = propsSubTitle || routeSubTitle;

	return (
		<Wrapper>
			<TitleBox>
				<Title>{title}</Title>
				{titleDescription && <TitleDescription>{titleDescription}</TitleDescription>}
				{link && (
					<Link onClick={() => window.open(link, '_blank')}>
						<IoIosLink />
					</Link>
				)}
			</TitleBox>
			<FlexBox>
				<SubTitle>{subTitle}</SubTitle>
				<ButtonBox>
					{isSearch && <Button onClick={() => setSearchModal(true)}>검색</Button>}
					{buttonList?.map((button, index) => {
						if (!button) return null;

						// 스타일 분기는 문자열 라벨에만 적용한다 (컴포넌트 라벨은 자기 스타일을 스스로 책임진다)
						const label = typeof button === 'string' ? button : '';

						return (
							<Button
								key={`${index}-button`}
								$fontColor={
									label.includes('전체 로그아웃') ? theme.colors.activeRedOrange : theme.colors.deepGrayFontColor
								}
								$delete={label.includes('삭제')}
								// disabled={buttonDisabled(button as string)}
								onClick={buttonHandler[index]}>
								{button}
							</Button>
						);
					})}
				</ButtonBox>
			</FlexBox>
			<SearchBox $active={!!search}>
				{search && (
					<>
						<SearchTerm>
							{search}
							<DeleteIcon onClick={() => setSearch('')}>
								<IoCloseOutline />
							</DeleteIcon>
						</SearchTerm>
						<CountText>{searchCount?.toLocaleString()}개의 검색결과</CountText>
					</>
				)}
			</SearchBox>
			{searchModal && <SearchModal setSearchModal={setSearchModal} onSearch={handleSearch} />}
		</Wrapper>
	);
};

export default Headline;

const Wrapper = styled.div`
	width: 100%;
	padding-bottom: 30px;
`;
const TitleBox = styled.div`
	display: flex;
`;
const Title = styled.h3`
	font-family: PretendardSemiBold, AppleSDGothicNeoSemiBold, sans-serif;
	margin-bottom: 10px;
`;
const TitleDescription = styled.div`
	color: ${theme.colors.activeRedOrange};
	margin-left: 4px;
`;
const Link = styled.span`
	color: ${theme.colors.activeBlue};
	margin-left: 8px;
	cursor: pointer;

	&:hover {
		color: ${theme.colors.activeSkyBlue};
	}
`;
const FlexBox = styled.div`
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: space-between;
`;
const SubTitle = styled.div`
	font-size: 14px;
	color: ${theme.colors.deepGrayFontColor};
`;
const ButtonBox = styled.div`
	display: flex;
	align-items: center;
	gap: 0 20px;
`;
const Button = styled.button<{ $delete?: boolean; $fontColor?: string }>`
	min-width: 38px;
	font-size: 14px;
	color: ${({ $fontColor }) => ($fontColor ? $fontColor : theme.colors.deepGrayFontColor)};
	transition: 0.25s;

	&:hover {
		font-weight: 500;
		color: ${darken(0.35, theme.colors.deepGrayFontColor)};
	}

	${({ $delete }) =>
		$delete &&
		css`
			&:hover {
				font-weight: 500;
				color: ${theme.colors.activeRedOrange};
			}
		`};

	&:disabled {
		&:hover {
			color: ${theme.colors.deepGrayFontColor};
			cursor: initial;
		}
	}
`;
const SearchBox = styled.div<{ $active?: boolean }>`
	width: 100%;
	transition: 0.25s;

	${({ $active }) =>
		$active &&
		css`
			padding-top: 30px;
		`};
`;
const SearchTerm = styled.div`
	font-size: 16px;
	font-family: PretendardSemiBold, AppleSDGothicNeoSemiBold, sans-serif;
	display: flex;
`;
const DeleteIcon = styled.div`
	margin-left: 8px;
	cursor: pointer;
`;
const CountText = styled.div`
	color: ${theme.colors.deepGrayFontColor};
	margin-top: 10px;
`;
